import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useSelector, useDispatch } from "react-redux";
import AlertDialog from '../components/AlertDialog';
import CircularLoading from "../components/CircularLoading";
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Grid from '@mui/material/Grid';
import { api } from 'common';
import { useTranslation } from "react-i18next";
import CountryListSelect from '../components/CountryListSelect';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InputAdornment from '@mui/material/InputAdornment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import LoginIcon from '@mui/icons-material/Login';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SaveIcon from '@mui/icons-material/Save';
import CircularProgress from '@mui/material/CircularProgress';
import { PanicSettings, BookingImageSettings , DeliveryFlow} from 'common/sharedFunctions';
import { DispatchSettings } from 'common/sharedFunctions';
import { showEst } from 'common/sharedFunctions';
import { optionsRequired } from 'common/sharedFunctions';
import { FONT_FAMILY } from "../common/sharedFunctions";
import { makeStyles } from '@mui/styles';

const modernColors = {
  primary: '#FFC107',
  primaryLight: '#FFD54F',
  primaryDark: '#FFA000',
  secondary: '#10B981',
  secondaryLight: '#34D399',
  background: '#F9FAFB',
  surface: '#FFFFFF',
  accent: '#4F46E5',
  textPrimary: '#1F2937',
  textSecondary: '#4B5563',
  divider: '#E5E7EB',
  hover: '#FFFBEB',
  active: '#FFF8E1',
  error: '#EF4444',
  info: '#3B82F6',
  white: '#FFFFFF',
};

const useStyles = makeStyles({
  textField: {
    "& label.Mui-focused": {
      color: modernColors.primary,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: modernColors.primary,
    },
    "& .MuiFilledInput-underline:after": {
      borderBottomColor: modernColors.primary,
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: modernColors.primary,
      },
    },
    "& input": {
      fontFamily: FONT_FAMILY,
    },
  },
  rootRtl_1: {
    "& label": {
      right: 15,
      left: "auto",
      paddingRight: 25,
      fontFamily: FONT_FAMILY,
    },
    "& legend": {
      textAlign: "right",
      marginRight: 25,
      fontFamily: FONT_FAMILY,
    },
    "& label.Mui-focused": {
      color: modernColors.primary,
      fontFamily: FONT_FAMILY,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: modernColors.primary,
    },
    "& .MuiFilledInput-underline:after": {
      borderBottomColor: modernColors.primary,
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: modernColors.primary,
      },
    },
  },
  right: {
    "& legend": {
      marginRight: 30,
    },
  },
});

const GeneralSettings = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    editSettings,
    clearSettingsViewError,
    countries
  } = api;
  const settingsdata = useSelector(state => state.settingsdata);
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [clicked, setClicked] = useState(false);
  const [countryCode, setCountryCode] = useState(null);
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    if (settingsdata.settings) {
      setData(settingsdata.settings);
    }
  }, [settingsdata.settings]);

  useEffect(() => {
    if (data && countries && countries.length > 0) {
      const currentCountry = countries.find(c => c.label === data.country);
      setCountryCode(currentCountry || null);
      const restrictedCountry = countries.find(c => c.code === data.restrictCountry);
      setCountry(restrictedCountry || null);
    }
  }, [data, countries]);

  const handleSwitchChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.checked });
  };

  const handleTextChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const handleNumberChange = (e, allowDecimal = false) => {
    const { name, value } = e.target;
    const regex = allowDecimal ? /^[0-9]*\.?[0-9]*$/ : /^[0-9]*$/;
    if (value === '' || regex.test(value)) {
      setData({ ...data, [name]: value });
    }
  };

  const handleCountryChange = (value) => {
    setData({ ...data, country: value ? value.label : null });
    setCountryCode(value);
  };

  const handleCountryCode = (value) => {
    setData({ ...data, restrictCountry: value ? value.code : null });
    setCountry(value);
  };

  const handleBonusChange = (e) => {
    handleNumberChange(e, true);
  };

  const handleDecimalChange = (e) => {
    handleNumberChange(e);
  };

  const handleDriverRadius = (e) => {
    handleNumberChange(e, true);
  };

  const handleThreshold = (e) => {
    handleNumberChange(e, true);
  };

  const handleWalletMoney = (e) => {
    handleNumberChange(e, true);
  };

  const handleChange = (e) => {
    setData({ ...data, bookingFlow: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (data.AllowCriticalEditsAdmin) {
      if((data.mobileLogin === false && data.emailLogin === true) || (data.mobileLogin === true && data.emailLogin === false) || (data.mobileLogin === true && data.emailLogin === true)) {
          setClicked(true);
        setLoading(true);
          dispatch(editSettings(data));
      } else {
        alert(t('mobile_or_email_cant_off'));
      }
    } else {
      alert(t('demo_mode'));
    }
  };

  const handleClose = () => {
    setClicked(false);
    setLoading(false);
    dispatch(clearSettingsViewError());
  };

  useEffect(() => {
    if (clicked && !settingsdata.loading) {
      setLoading(false);
    }
  }, [settingsdata.loading, clicked]);

  const renderSectionHeader = (title, icon) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      {icon}
      <Typography variant="h6" sx={{ fontWeight: 500, color: modernColors.textPrimary, fontFamily: FONT_FAMILY }}>
        {title}
                </Typography>
    </Box>
  );

  const renderSwitch = (name, labelKey) => (
                <FormControlLabel
                  control={
                    <Switch
          checked={data?.[name] || false}
                      onChange={handleSwitchChange}
          name={name}
                      color="primary"
          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: modernColors.primary }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: modernColors.primary } }}
                    />
                  }
      label={<Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.95rem', color: modernColors.textSecondary }}>{t(labelKey)}</Typography>}
      sx={{ mr: 1, mb: 1 }}
    />
  );

  const renderTextField = (name, labelKey, required = false, type = 'text', icon = null, valueProcessor = handleTextChange, extraProps = {}) => (
                    <TextField
                      variant="outlined"
                      margin="normal"
      required={required}
                      fullWidth
      id={name}
      label={t(labelKey)}
      name={name}
      type={type}
      autoComplete={name}
      onChange={valueProcessor}
      value={data?.[name] || ''}
      InputProps={{
        startAdornment: icon ? <InputAdornment position="start">{icon}</InputAdornment> : null,
        sx: { borderRadius: '10px', backgroundColor: modernColors.white }
      }}
      sx={{ mb: 1, '& .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.divider }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.primaryLight }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.primary } }}
      {...extraProps}
    />
  );

  return (
    !data ? <CircularLoading /> :
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: '12px', backgroundColor: modernColors.surface }}>
      <Typography variant="h5" sx={{ fontWeight: 600, color: modernColors.textPrimary, mb: 3, fontFamily: FONT_FAMILY }}>
        {t('general_settings')}
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
        <Accordion defaultExpanded sx={{ boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${modernColors.divider}`, borderRadius: '10px', mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="currency-settings-content" id="currency-settings-header">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachMoneyIcon sx={{ color: modernColors.primary }} />
              <Typography sx={{ fontWeight: 500, fontFamily: FONT_FAMILY, color: modernColors.textPrimary }}>{t('currency_settings')}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                {renderTextField("symbol", "currency_symbol", true, 'text')}
              </Grid>
              <Grid item xs={12} sm={4}>
                {renderTextField("code", "currency_code", true, 'text')}
              </Grid>
              <Grid item xs={12} sm={4}>
                {renderTextField("decimal", "set_decimal", false, 'number', null, handleDecimalChange)}
              </Grid>
              <Grid item xs={12}>
                {renderSwitch("swipe_symbol", "swipe_symbol")}
                {renderSwitch("disable_online", "disable_online")}
                {renderSwitch("disable_cash", "disable_cash")}
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
        <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${modernColors.divider}`, borderRadius: '10px', mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="security-settings-content" id="security-settings-header">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon sx={{ color: modernColors.secondary }} />
              <Typography sx={{ fontWeight: 500, fontFamily: FONT_FAMILY, color: modernColors.textPrimary }}>{t('security_title')}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={4}>
                {renderSwitch("otp_secure", "settings_label3")}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                {renderSwitch("driver_approval", "settings_label4")}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                {renderSwitch("carApproval", "settings_label5")}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                {renderSwitch("imageIdApproval", "settings_label6")}
                  </Grid>
                </Grid>
          </AccordionDetails>
        </Accordion>
        <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${modernColors.divider}`, borderRadius: '10px', mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="bonus-wallet-settings-content" id="bonus-wallet-settings-header">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MonetizationOnIcon sx={{ color: modernColors.primaryDark }} />
              <Typography sx={{ fontWeight: 500, fontFamily: FONT_FAMILY, color: modernColors.textPrimary }}>{t('Referral, Wallet & Threshold')}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                {renderTextField("bonus", "referral_bonus", false, 'number', <CardGiftcardIcon sx={{color: modernColors.textSecondary}} />, handleBonusChange, { inputProps: { step: "0.01" }})}
              </Grid>
              <Grid item xs={12} md={4}>
                {renderTextField("walletMoneyField", "wallet_money_field", false, 'number', <AccountBalanceWalletIcon sx={{color: modernColors.textSecondary}} />, handleWalletMoney, { inputProps: { step: "0.01" }})}
              </Grid>
              <Grid item xs={12} md={4}>
                {renderTextField("driverThreshold", "driver_threshold", false, 'number', null, handleThreshold, { inputProps: { step: "0.01" }})}
              </Grid>
              <Grid item xs={12}>
                {renderSwitch("RiderWithDraw", "rider_withdraw")}
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
        <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${modernColors.divider}`, borderRadius: '10px', mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="advanced-settings-content" id="advanced-settings-header">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon sx={{ color: modernColors.info }} />
              <Typography sx={{ fontWeight: 500, fontFamily: FONT_FAMILY, color: modernColors.textPrimary }}>{t('advance_settings')}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
                <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                    <CountryListSelect
                      label={t('select_country')}
                  dis={false}
                      countries={countries}
                      onlyCode={false}
                  value={countryCode}
                  onChange={(object, value) => handleCountryChange(value)}
                  sx={{ mt: 1, mb:1, '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: modernColors.white }, '& .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.divider }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.primaryLight }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.primary } }}
                    />
                  </Grid>
              <Grid item xs={12} sm={6}>
                    <CountryListSelect
                      label={t('country_restriction')}
                  dis={false}
                      countries={countries}
                      onlyCode={true}
                  value={country}
                  onChange={(object, value) => handleCountryCode(value)}
                  sx={{ mt: 1, mb:1, '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: modernColors.white }, '& .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.divider }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.primaryLight }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: modernColors.primary } }}
                />
              </Grid>
              <Grid item xs={12}>
                {renderSwitch("AllowCountrySelection", "allow_multi_country")}
                {renderSwitch("convert_to_mile", "convert_to_mile")}
                {renderSwitch("horizontal_view", "car_view_horizontal")}
                {renderSwitch("useDistanceMatrix", "use_distance_matrix")}
                {renderSwitch("CarHornRepeat", "car_horn_repeat")}
                {showEst && (!optionsRequired) && renderSwitch("prepaid", "prepaid")}
              </Grid>
              <Grid item xs={12}>
                <BookingImageSettings data={data} handleSwitchChange={handleSwitchChange} classes={classes} />
              </Grid>
              <Grid item xs={12}>
                <DeliveryFlow data={data.bookingFlow} handleChange={handleChange} classes={classes} />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
        <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${modernColors.divider}`, borderRadius: '10px', mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="login-settings-content" id="login-settings-header">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LoginIcon sx={{ color: modernColors.accent }} />
              <Typography sx={{ fontWeight: 500, fontFamily: FONT_FAMILY, color: modernColors.textPrimary }}>{t('login_settings')}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                {renderSwitch("emailLogin", "email_login")}
              </Grid>
              <Grid item xs={12} sm={4}>
                {renderSwitch("mobileLogin", "mobile_login")}
              </Grid>
              <Grid item xs={12} sm={4}>
                {renderSwitch("socialLogin", "social_login")}
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
        <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${modernColors.divider}`, borderRadius: '10px', mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="driver-settings-content" id="driver-settings-header">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DriveEtaIcon sx={{ color: modernColors.secondary }} />
              <Typography sx={{ fontWeight: 500, fontFamily: FONT_FAMILY, color: modernColors.textPrimary }}>{t('driver_setting')}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {renderTextField("driverRadius", "driverRadius", false, 'number', <MyLocationIcon sx={{color: modernColors.textSecondary}} />, handleDriverRadius, { inputProps: { step: "0.1" }})}
              </Grid>
              <Grid item xs={12} md={6} sx={{display: 'flex', alignItems: 'center'}}>
                <DispatchSettings autoDispatch={data.autoDispatch} onChange={handleSwitchChange} />
              </Grid>
              <Grid item xs={12}>
                {renderSwitch("negativeBalance", "negative_balance")}
                {renderSwitch("realtime_drivers", "realtime_drivers")}
                {renderSwitch("bank_fields", "bank_fields")}
                {renderSwitch("showLiveRoute", "show_live_route")}
                {renderSwitch("carType_required", "carType_required")}
                {renderSwitch("term_required", "term_required")}
                {renderSwitch("license_image_required", "license_image_required")}
              </Grid>
        </Grid>
          </AccordionDetails>
        </Accordion>
        <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${modernColors.divider}`, borderRadius: '10px', mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panic-settings-content" id="panic-settings-header">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HelpOutlineIcon sx={{ color: modernColors.error }} />
              <Typography sx={{ fontWeight: 500, fontFamily: FONT_FAMILY, color: modernColors.textPrimary }}>{t('panic_settings')}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <PanicSettings data={data} handleTextChange={handleTextChange} classes={classes} />
          </AccordionDetails>
        </Accordion>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} sx={{ color: modernColors.textPrimary }}/> : <SaveIcon />}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: '10px',
              backgroundColor: modernColors.primary,
              color: modernColors.textPrimary,
              fontFamily: FONT_FAMILY,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: modernColors.primaryDark,
                boxShadow: `0 4px 12px ${modernColors.primary}40`,
              }
            }}
          >
            {loading ? t('saving') : t('submit')}
          </Button>
        </Box>
      </Box>
      <AlertDialog open={settingsdata.error.flag && clicked} onClose={handleClose}>
        <Typography sx={{fontFamily: FONT_FAMILY}}>{t('update_failed')}</Typography>
      </AlertDialog>
    </Paper>
  );
};

export default GeneralSettings;
